import { Routes } from '@common/constants';
import { ListPage } from '@features/List';
import { useRoomPageSettings } from '@features/rooms';
import { usePackagesSpaAvailability } from '@features/spa/hooks/useGetPackagesSpaAvailability';
import { useGetSpaStaysPage } from '@features/spa/hooks';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';
import { useLabels } from '@features/Menu/useLabels';
import { useModuleStatuses } from '@features/properties';
import { RatesFilterType, useRatePageSettings, useRates } from '@features/rates';
import { useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import { Moment } from 'moment';
import { FC } from 'react';
import { useRouter } from 'shared-routing';
import { PromoRateCode } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

type PackagesProps = {
  journeyParams: ParsedJourneyParams;
  changeJourneyParam: (paramName: string, value?: string | Moment | number | PromoRateCode) => void;
  property: Property;
  language: string;
  type?: RatesFilterType;
};

export const Packages: FC<PackagesProps> = ({
  journeyParams,
  changeJourneyParam,
  property,
  language,
  type,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const labels = useLabels();
  const isSpaStays = type === RatesFilterType.SPA_STAYS;
  const { content: roomsPageContent, isFetching: isFetchingRoomsContent } = useRoomPageSettings(
    language,
    t('common.rate')
  );

  const { content: ratesPageContent, isFetching: isFetchingRatesContent } = useRatePageSettings(
    language,
    t('common.rate')
  );

  const { data: spaStaysPage } = useGetSpaStaysPage(property.id, language, isSpaStays);

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();
  const acceptPromoRateCode = isRoomsEnabled;

  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const promoRateCode =
    typeof router.query.promoRateCode === 'string'
      ? router.query.promoRateCode
      : journeyParams.promoRateCode;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const { rates, isFetching, error } = useRates({
    fromDate,
    toDate,
    adults,
    children,
    promoRateCode,
    childAges,
    rooms,
    type,
    language,
    availabilityMode: 'listing',
  });

  const { data: availability, isFetching: isFetchingAvailability } = usePackagesSpaAvailability(
    property,
    rates,
    journeyParams,
    !isFetching
  );

  const rateListItems = rates
    .filter((rate) => !rate.unAvailable)
    .map((rate) => {
      const rateAvailability = availability ? availability[rate.id] : undefined;
      const isUnavailable = rateAvailability?.isUnavailable ?? false;

      return {
        ...rate,
        unAvailable: isUnavailable,
        linkRoute: isSpaStays ? Routes.SPA_STAYS_DETAIL : Routes.RATE_DETAIL,
        linkParams: {
          id: rate.id,
          fromDate,
          toDate,
          adults,
          children,
          promoRateCode,
        },
      };
    });
  const errorMessage = useRatesErrorMessage(error);

  const onChangeJourneyParams = (params) => {
    changeJourneyParam('fromDate', params.fromDate);
    changeJourneyParam('spaDate', params.fromDate);
    changeJourneyParam('toDate', params.toDate);
    changeJourneyParam(
      'adults',
      (params.rooms || []).reduce((acc, room) => acc + room.adults, 0) ?? params.adults
    );
    changeJourneyParam(
      'children',
      (params.rooms || []).reduce((acc, room) => acc + room.children, 0) ?? params.children
    );
    changeJourneyParam('promoRateCode', params.promoRateCode);
    changeJourneyParam('childAges', params.childAges);
    changeJourneyParam('rooms', params.rooms);
    const { promoRateCode, childAges: _childAges, rooms: _rooms, ...otherParams } = params;
    const { rateCode } = router.query;
    router.push({
      pathname: type === RatesFilterType.SPA_STAYS ? Routes.SPA_STAYS_LIST : Routes.RATE_LIST,
      query: {
        ...otherParams,
        ...(promoRateCode ? { promoRateCode } : undefined),
        ...(rateCode ? { rateCode } : undefined),
      } as any,
    });
  };

  return (
    <ListPage
      journeyParams={journeyParams}
      onChangeJourneyParams={onChangeJourneyParams}
      title={
        isSpaStays
          ? spaStaysPage?.title ?? labels?.spastays ?? t('flynav.spaStays')
          : ratesPageContent.title ?? roomsPageContent.title
      }
      description={
        isSpaStays
          ? spaStaysPage?.content ?? null
          : ratesPageContent.description ?? roomsPageContent.description
      }
      items={rateListItems}
      isFetching={
        isFetching || isFetchingRoomsContent || isFetchingRatesContent || isFetchingAvailability
      }
      errorMessage={errorMessage}
      acceptPromoRateCode={acceptPromoRateCode}
    />
  );
};
